import { computed, makeObservable } from 'mobx'
import type { $Values } from 'utility-types'

import buildUrl from 'modules/core/lib/buildUrl'
import apiClient from 'modules/core/lib/mobx-rest/apiClient'
import { ModelWithPolicy } from 'modules/core/lib/modelWithPolicy'
import DocumentPolicy from 'modules/core/store/models/policies/DocumentPolicy'

import { SPACES } from 'modules/documents/lib/constants'
import { ESIGNATURE_FILE_TYPE } from 'modules/documents/lib/constants'
import { NotificationStatuses } from 'modules/documents/lib/enums'
import folders from 'modules/documents/store/collections/folders'
import type Folder from 'modules/documents/store/models/Folder'

export const ALL_TYPES = {
  default: 'default',
  adherence: 'adherence',
  contract: 'contract',
  hiring: 'hiring',
  id_document: 'id-document',
  modelo_145: 'modelo-145',
  social_insurance: 'social_insurance',
  novation: 'novation',
  payment: 'payment',
  payslip: 'payslip',
  sick_leave: 'sick-leave',
  tax: 'tax',
  expenses_company: 'expenses_company',
  expenses_employee: 'expenses_employee',
}

export type DocumentType = $Values<typeof ALL_TYPES>

export interface IDocument {
  [key: string]: any
}

class Document extends ModelWithPolicy<IDocument> {
  copyDocument() {
    throw new Error('Method not implemented.')
  }
  static TYPES = ALL_TYPES

  constructor(attr: IDocument = {}) {
    super(attr)
    makeObservable(this, {
      type: computed,
      folder: computed,
      folderId: computed,
      folderName: computed,
    })
  }

  get policy() {
    return new DocumentPolicy(this)
  }

  // TODO: Try to remove this type duplication in backend
  get type(): DocumentType {
    const type = this.attributes.type

    return type === ALL_TYPES.hiring ? ALL_TYPES.contract : type
  }

  get filename(): string {
    return this.attributes.filename
  }

  get file(): string {
    return this.attributes.file
  }

  assignedToMe(currentEmployeeId: number): boolean {
    return currentEmployeeId === this.attributes.employee_id
  }

  get folder(): Folder | null | undefined {
    return folders.get(this.attributes.folder_id)
  }

  get id(): number {
    return this.attributes.id
  }

  get folderId(): number {
    return this.attributes.folder_id
  }

  get folderName(): string {
    return this.folder?.attributes.name
  }

  get employeeId(): number {
    return this.attributes.employee_id
  }

  get contentType(): string {
    return this.attributes.content_type
  }

  get extension(): string {
    return this.attributes.extension
  }

  get signees(): number[] {
    return this.attributes.signees
  }

  get space(): string {
    return this.attributes.space
  }

  get notificationStatus(): NotificationStatuses {
    return this.attributes.notification_status
  }

  get isTemplate(): boolean {
    return !!this.attributes.template_id || this.attributes.from_template
  }

  get isSignable(): boolean {
    return this.contentType === ESIGNATURE_FILE_TYPE
  }

  get isFillablePDF(): boolean {
    return this.isPendingToFill || this.isFilled
  }

  get isPendingToFill(): boolean {
    return this.attributes.status == 'to_fill'
  }

  get isFilled(): boolean {
    return this.attributes.status == 'filled'
  }

  canFill(currentEmployeeId: number): boolean {
    return this.isPendingToFill && this.assignedToMe(currentEmployeeId)
  }

  get isCompletedSignatures(): boolean {
    return this.signatureStatus === 'completed'
  }

  get isDeclinedSignatures(): boolean {
    return this.signatureStatus === 'declined'
  }

  get isPendingSignatures(): boolean {
    return this.signatureStatus === 'pending'
  }

  get isBouncedSignatures(): boolean {
    return this.signatureStatus === 'bounced_email'
  }

  get isCancelledSignatures(): boolean {
    return this.signatureStatus === 'cancelled'
  }

  get isPartiallySignedSignatures(): boolean {
    return this.signatureStatus === 'partially_signed'
  }

  get isErrorSignatures(): boolean {
    return this.signatureStatus === 'error'
  }

  get signatureStatus(): string {
    return this.attributes.signature_status
  }

  get isManagementCompanyDocument(): boolean {
    return this.space === SPACES.COMPANY_INTERNAL
  }

  get isPublicCompanyDocument(): boolean {
    return this.space === SPACES.COMPANY_PUBLIC
  }

  get isCompanyDocument(): boolean {
    return this.attributes.is_company_document
  }

  get isInTrashBin(): boolean {
    return this.attributes.deleted_at !== undefined && this.attributes.deleted_at !== null
  }

  upload() {
    return this.rpc('upload_from_document', { id: `${this.id}` })
  }

  get bookkeeperDownloadUrl(): string {
    const url = new URL(buildUrl(`${this.attributes.file}`))
    url.searchParams.set('bookkeeper_view', 'true')

    return url.toString()
  }

  static urlRoot() {
    return '/documents'
  }

  async updateFillable(file: Blob): Promise<Document> {
    const url = `/fillable_documents/${this.id}`

    const form = new FormData()
    form.append('file', file, this.attributes.filename)

    return await apiClient().put(url, form)
  }
}

export default Document
